import type { CSSProperties, ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';

import type { SerializableNode } from '@/serializer/Serializer.types';

import type {
  Breadcrumb,
  CustomAttribute,
  DreamRenderElementProps,
  EmptyChildren,
  PostHeaderCardStructure,
  PostHeaderData,
  PostTheme,
  UTMAttrs,
  WebTheme,
} from '../types';

export type TPostHeaderAttributes = {
  id: string;
  gap?: number;
  mobileGap?: CSSProperties['gap'];
  height?: number;
  width?: number;
  content: EmptyChildren;
  customAttributes?: CustomAttribute;
  margin?: CSSProperties['margin'];
  padding?: CSSProperties['padding'];
  textAlign?: CSSProperties['textAlign'];
  borderRadius?: CSSProperties['borderRadius'];
  borderWidth?: CSSProperties['borderWidth'];
  borderColor?: CSSProperties['borderColor'];
  borderStyle?: CSSProperties['borderStyle'];
  dividerColor?: CSSProperties['borderColor'];
  dividerStyle?: CSSProperties['borderStyle'];
  dividerThickness?: string;
  shadow?: CSSProperties['boxShadow'];
  hoverShadow?: CSSProperties['boxShadow'];
  background?: CSSProperties['backgroundColor'];
  color?: CSSProperties['color'];
  cardPadding?: CSSProperties['padding'];

  separatorColor?: CSSProperties['color'];
  byLineText?: string;

  // Image
  imageEnabled?: boolean;
  imageSizeFixed?: boolean;
  imageWidth?: string;
  imageHeight?: string;
  imageBorderRadius?: CSSProperties['borderRadius'];
  backgroundImageFilterColor?: CSSProperties['backgroundColor'];
  hasBackgroundImage?: boolean;

  // Tags
  tagsEnabled?: boolean;
  tagTextColor?: CSSProperties['color'];
  tagTransform?: CSSProperties['textTransform'];
  tagBadgeEnabled?: boolean;
  tagBackgroundColor?: CSSProperties['backgroundColor'];
  tagBorderRadius?: CSSProperties['borderRadius'];
  tagFontFamily?: CSSProperties['fontFamily'];
  tagFontWeight?: CSSProperties['fontWeight'];

  // Titles
  titleTextColor?: CSSProperties['color'];
  titleFontSize?: string;
  titleFontWeight?: CSSProperties['fontWeight'];
  titleLineClamp?: CSSProperties['lineClamp'];
  titleFontFamily?: CSSProperties['fontFamily'];
  titleTransform?: CSSProperties['textTransform'];
  subtitleShowing?: boolean;
  subtitleTextColor?: CSSProperties['color'];
  subtitleFontSize?: string;
  subtitleFontWeight?: CSSProperties['fontWeight'];
  subtitleLineClamp?: CSSProperties['lineClamp'];
  subtitleFontFamily?: CSSProperties['fontFamily'];
  subtitleTransform?: CSSProperties['textTransform'];

  // Authors
  authorsEnabled?: boolean;
  authorsImagesEnabled?: boolean;
  authorsTextEnabled?: boolean;
  authorsTruncateListEnabled?: boolean;
  authorsTextColor?: CSSProperties['color'];
  authorsTextTransform?: CSSProperties['textTransform'];
  authorsFontSize?: string;
  authorsFontWeight?: CSSProperties['fontWeight'];
  authorsFontFamily?: CSSProperties['fontFamily'];

  // Timestamps
  timestampsEnabled?: boolean;
  timestampsTextColor?: CSSProperties['color'];
  timestampsDisplay?: string;
  timestampsFontWeight?: CSSProperties['fontWeight'];

  // Read Time
  readTimeEnabled?: boolean;
  readTimeTextColor?: CSSProperties['color'];

  // Premium
  premiumIconEnabled?: boolean;
  premiumTextEnabled?: boolean;
  premiumBadgeEnabled?: boolean;
  premiumTextColor?: CSSProperties['color'];
  premiumBackgroundColor?: CSSProperties['backgroundColor'];
  premiumTextTransform?: CSSProperties['textTransform'];
  premiumFontWeight?: CSSProperties['fontWeight'];

  // Structure
  cardStructure?: PostHeaderCardStructure;

  structureTiptapState?: JSONContent & SerializableNode;
  structureTiptapStateDraft?: JSONContent & SerializableNode;

  data: {
    post: PostHeaderData;
    breadcrumbs: Breadcrumb[];
    shareUrl: string;
  };

  hasFakeData?: boolean;
};

export type TPostHeaderElement = {
  type: 'postHeader';
  attrs: TPostHeaderAttributes;
};

export const WEB_HEADER_TITLE_ATTR = 'data-web-header-title';
export const WEB_HEADER_SUBTITLE_ATTR = 'data-web-header-subtitle';

export type PostHeaderElementProps = DreamRenderElementProps<TPostHeaderElement> & {
  webTheme?: WebTheme;
  postTheme?: PostTheme;
  isLive?: boolean;
  disableEmbeds?: boolean;
  utmParams?: UTMAttrs;
  editorMode?: boolean;
  onTitleChange?: (value: string) => void;
  onSubtitleChange?: (value: string) => void;
  isTitleSelected?: boolean;
  isSubtitleSelected?: boolean;
  renderBoundImage?: (reference: string) => ReactNode;
};
